/**
 * Module-level API prefetch — fires immediately on import (before React mounts).
 * Contexts can consume these promises instead of making duplicate requests,
 * eliminating the sequential wait: JS load → React mount → context fetch.
 */
import api, { apiRoutes } from './index';
import { getConfig } from '../config/environment';

const { tlfId } = getConfig();
const config = getConfig();
const UPCOMING_DRAWS_CACHE_TTL_MS = 10_000;
const upcomingDrawsCache = new Map();

// Fire critical API calls at module load time.
// Attach a no-op catch to prevent unhandled rejection events if consumers
// (like GeoLocationContext or SiteConfigContext) ignore or bypass them.
const currentDomain = typeof window !== 'undefined' && window.location ? window.location.hostname : '';

export const prefetchedSiteDetails = tlfId
  ? api.get(apiRoutes.SiteDetails, { params: { organizationId: tlfId, domain: currentDomain } })
  : null;
if (prefetchedSiteDetails?.catch) {
  prefetchedSiteDetails.catch(() => {});
}

export function fetchUpcomingDraws(organizationId) {
  const cached = upcomingDrawsCache.get(organizationId);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;

  const promise = api.get(apiRoutes.OrganizationUpcomingDraws, {
    params: { organizationId },
  }).catch((error) => {
    if (upcomingDrawsCache.get(organizationId)?.promise === promise) {
      upcomingDrawsCache.delete(organizationId);
    }
    throw error;
  });

  upcomingDrawsCache.set(organizationId, {
    promise,
    expiresAt: Date.now() + UPCOMING_DRAWS_CACHE_TTL_MS,
  });
  return promise;
}

export async function fetchNextDrawIfAvailable(organizationId) {
  const drawsResponse = await fetchUpcomingDraws(organizationId);
  const payload = drawsResponse.data?.payload || drawsResponse.data;
  const draws = payload?.data || payload?.Data || payload || [];

  if (!Array.isArray(draws) || draws.length === 0) return null;

  return api.get(apiRoutes.OrganizationNextDraw, { params: { organizationId } });
}

export const prefetchedNextDraw = tlfId
  ? fetchNextDrawIfAvailable(tlfId)
  : null;
if (prefetchedNextDraw?.catch) {
  prefetchedNextDraw.catch(() => {});
}

// Prefetch geo/location check by IP (no browser coords yet).
// For bots/denied users this is the only call needed.
const ip = (config.clientIp || '').split(':')[0];
export const prefetchedLocationAccess = (tlfId && config.requireLocation)
  ? api.get(apiRoutes.LocationAccess, { params: { organizationId: tlfId, ip } })
  : null;
if (prefetchedLocationAccess?.catch) {
  prefetchedLocationAccess.catch(() => {});
}
